import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';

const emptyCreate = { name: '', employeeId: '', email: '', password: '', department: '' };

const AdminEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);

  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', employeeId: '', email: '', department: '' });

  const [resetEmp, setResetEmp] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  const [deleteEmp, setDeleteEmp] = useState(null);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/api/admin/employees');
      setEmployees(res.data);
    } catch (e) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);

  const filtered = employees.filter((emp) =>
    (emp.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.employeeId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.department || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = async () => {
    if (!createForm.name || !createForm.employeeId || !createForm.password) {
      return toast.error('Name, Employee ID and Password are required');
    }
    setSaving(true);
    try {
      await axios.post('/api/admin/employees', createForm);
      toast.success('Employee created');
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEditForm({ name: emp.name || '', employeeId: emp.employeeId || '', email: emp.email || '', department: emp.department || '' });
  };

  const submitEdit = async () => {
    if (!editForm.name || !editForm.employeeId) return toast.error('Name and Employee ID are required');
    setSaving(true);
    try {
      await axios.patch(`/api/admin/employees/${editEmp._id}`, editForm);
      toast.success('Employee updated');
      setEditEmp(null);
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const submitReset = async () => {
    if (!resetPassword || resetPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setSaving(true);
    try {
      await axios.patch(`/api/admin/employees/${resetEmp._id}`, { password: resetPassword });
      toast.success('Password reset');
      setResetEmp(null);
      setResetPassword('');
    } catch (e) {
      toast.error('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (emp) => {
    try {
      await axios.patch(`/api/admin/employees/${emp._id}/status`, { isActive: emp.isActive === false });
      toast.success(emp.isActive === false ? 'Employee activated' : 'Employee deactivated');
      fetchEmployees();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteEmp) return;
    setSaving(true);
    try {
      await axios.delete(`/api/admin/employees/${deleteEmp._id}`);
      toast.success('Employee deleted');
      setDeleteEmp(null);
      fetchEmployees();
    } catch (e) {
      toast.error('Failed to delete employee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">{employees.length} employee(s) — manage access and details</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ Create Employee</button>
      </div>

      <div className="filters">
        <input
          className="form-input"
          placeholder="Search name, ID, email, department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="card-body">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No employees found</h3>
              <p className="empty-state-text">{employees.length === 0 ? 'Create your first employee' : 'Try adjusting your search'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Employee ID</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((emp) => (
                    <tr key={emp._id}>
                      <td style={{ fontWeight: 600 }}>{emp.name}</td>
                      <td>{emp.employeeId || '—'}</td>
                      <td>{emp.email}</td>
                      <td>{emp.department || '—'}</td>
                      <td>
                        <span className="badge" style={emp.isActive !== false ? { background: '#d1fae5', color: '#065f46' } : { background: '#fee2e2', color: '#991b1b' }}>
                          {emp.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(emp)}>Edit</button>{' '}
                        <button className="btn btn-outline btn-sm" onClick={() => { setResetEmp(emp); setResetPassword(''); }}>Reset</button>{' '}
                        <button className="btn btn-outline btn-sm" onClick={() => toggleStatus(emp)}>{emp.isActive !== false ? 'Deactivate' : 'Activate'}</button>{' '}
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteEmp(emp)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {createOpen && (
        <div className="modal-overlay" onClick={() => setCreateOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Create Employee</h3>
              <button className="modal-close" onClick={() => setCreateOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Employee Name *</label>
                <input className="form-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID *</label>
                <input className="form-input" value={createForm.employeeId} onChange={(e) => setCreateForm({ ...createForm, employeeId: e.target.value })} placeholder="e.g. EMP001" />
              </div>
              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input type="email" className="form-input" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input type="text" className="form-input" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-input" value={createForm.department} onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editEmp && (
        <div className="modal-overlay" onClick={() => setEditEmp(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Employee</h3>
              <button className="modal-close" onClick={() => setEditEmp(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Employee Name *</label>
                <input className="form-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID *</label>
                <input className="form-input" value={editForm.employeeId} onChange={(e) => setEditForm({ ...editForm, employeeId: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-input" value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditEmp(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetEmp && (
        <div className="modal-overlay" onClick={() => setResetEmp(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Reset Password</h3>
              <button className="modal-close" onClick={() => setResetEmp(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-500)', marginBottom: '12px' }}>
                Reset password for {resetEmp.name} ({resetEmp.employeeId || resetEmp.email})
              </p>
              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input type="text" className="form-input" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setResetEmp(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitReset} disabled={saving}>{saving ? 'Saving...' : 'Reset'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deleteEmp && (
        <div className="modal-overlay" onClick={() => setDeleteEmp(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Employee</h3>
              <button className="modal-close" onClick={() => setDeleteEmp(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                Are you sure you want to delete <strong>{deleteEmp.name}</strong> ({deleteEmp.employeeId || deleteEmp.email})? This will permanently remove the employee, their login, and all their expense records and proofs.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteEmp(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={saving}>{saving ? 'Deleting...' : 'Confirm Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmployees;
